import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, NavLink } from 'react-router-dom';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import './App.css';

// Import UI components
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Textarea } from './components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Badge } from './components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from './components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Set up axios defaults
axios.defaults.baseURL = API;

// Auth context
const AuthContext = React.createContext();

const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

// Login Component
const Login = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'employee'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const response = await axios.post(endpoint, formData);
      
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      // Set axios default authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      
      onLogin(response.data.user);
      toast.success(isLogin ? 'Login successful!' : 'Registration successful!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
          <div className="text-center mb-8">
            <img 
              src="https://images.unsplash.com/photo-1555212697-194d092e3b8f" 
              alt="TaskTime" 
              className="w-16 h-16 mx-auto rounded-full object-cover mb-4"
            />
            <h1 className="text-3xl font-bold text-gray-900 mb-2">TaskTime</h1>
            <p className="text-gray-600">Manage tasks and track time efficiently</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required={!isLogin}
                  className="mt-1"
                />
              </div>
            )}
            
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                required
                className="mt-1"
              />
            </div>

            {!isLogin && (
              <div>
                <Label htmlFor="role">Role</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="employee">Employee</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700">
              {isLogin ? 'Sign In' : 'Sign Up'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-indigo-600 hover:text-indigo-500 text-sm font-medium"
            >
              {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Task Board Component
const TaskBoard = () => {
  const { user } = useAuth();
  const [board, setBoard] = useState({
    pending: [],
    in_progress: [],
    waiting: [],
    completed: []
  });
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  const statusConfig = {
    pending: { title: 'To Do', color: 'bg-gray-100', badge: 'bg-gray-500' },
    in_progress: { title: 'In Progress', color: 'bg-blue-100', badge: 'bg-blue-500' },
    waiting: { title: 'Waiting', color: 'bg-yellow-100', badge: 'bg-yellow-500' },
    completed: { title: 'Completed', color: 'bg-green-100', badge: 'bg-green-500' }
  };

  const priorityColors = {
    low: 'bg-gray-100 text-gray-800',
    normal: 'bg-blue-100 text-blue-800',
    high: 'bg-orange-100 text-orange-800',
    urgent: 'bg-red-100 text-red-800'
  };

  useEffect(() => {
    fetchBoard();
    fetchUsers();
    fetchDepartments();
  }, []);

  const fetchBoard = async (silent = false) => {
    try {
      const response = await axios.get('/tasks/board');
      setBoard(response.data);
    } catch (error) {
      if (!silent) {
        toast.error('Failed to fetch tasks');
      }
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get('/departments');
      setDepartments(response.data);
    } catch (error) {
      console.error('Failed to fetch departments:', error);
    }
  };

  const onDragEnd = async (result) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    // Find the task being moved
    const sourceColumn = board[source.droppableId];
    const task = sourceColumn.find(t => String(t.id) === draggableId);

    if (!task) return;

    // Update local state IMMEDIATELY for smooth UX
    const newBoard = { ...board };
    
    // Remove from source
    newBoard[source.droppableId] = sourceColumn.filter(t => t.id !== draggableId);
    
    // Add to destination at the correct index
    const updatedTask = { ...task, status: destination.droppableId };
    const destColumn = [...newBoard[destination.droppableId]];
    destColumn.splice(destination.index, 0, updatedTask);
    newBoard[destination.droppableId] = destColumn;
    
    // Update UI instantly
    setBoard(newBoard);

    // Then update backend in background
    try {
      await axios.put(`/tasks/${task.id}`, { status: destination.droppableId });
      toast.success(`Task moved to ${destination.droppableId.replace('_', ' ')}`);
    } catch (error) {
      // Only revert on error
      toast.error('Failed to update task status - reverting change');
      fetchBoard(); // This will refresh from server to revert the change
    }
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    // Find the task and update locally first for instant feedback
    let taskToUpdate = null;
    let sourceStatus = null;
    
    // Find task in current board state
    for (const [status, tasks] of Object.entries(board)) {
      const task = tasks.find(t => t.id === taskId);
      if (task) {
        taskToUpdate = task;
        sourceStatus = status;
        break;
      }
    }
    
    if (!taskToUpdate || !sourceStatus) return;

    // Update UI immediately
    const newBoard = { ...board };
    
    // Remove from source
    newBoard[sourceStatus] = newBoard[sourceStatus].filter(t => t.id !== taskId);
    
    // Add to destination
    const updatedTask = { ...taskToUpdate, status: newStatus };
    newBoard[newStatus] = [...newBoard[newStatus], updatedTask];
    
    setBoard(newBoard);

    // Update backend in background
    try {
      await axios.put(`/tasks/${taskId}`, { status: newStatus });
      toast.success('Task status updated');
    } catch (error) {
      toast.error('Failed to update task status - reverting change');
      fetchBoard(); // Revert on error
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Task Board</h1>
            <p className="text-gray-600 mt-1">Manage and track your team's tasks</p>
          </div>
          <div className="flex items-center gap-4">
            <Button onClick={() => setShowCreateTask(true)} className="bg-indigo-600 hover:bg-indigo-700">
              + Create Task
            </Button>
            <Avatar>
              <AvatarImage src={user?.avatar_url} />
              <AvatarFallback>{user?.name?.charAt(0) || 'U'}</AvatarFallback>
            </Avatar>
          </div>
        </div>
      </div>

      {/* Task Board */}
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Object.entries(statusConfig).map(([status, config]) => (
            <TaskColumn
              key={status}
              status={status}
              config={config}
              tasks={board[status] || []}
              priorityColors={priorityColors}
              onTaskClick={setSelectedTask}
              onStatusUpdate={updateTaskStatus}
            />
          ))}
        </div>
      </DragDropContext>

      {/* Create Task Modal */}
      {showCreateTask && (
        <CreateTaskModal
          users={users}
          departments={departments}
          onClose={() => setShowCreateTask(false)}
          onSuccess={() => {
            setShowCreateTask(false);
            fetchBoard();
          }}
        />
      )}

      {/* Task Details Modal */}
      {selectedTask && (
        <TaskDetailsModal
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onSuccess={fetchBoard}
        />
      )}
    </div>
  );
};

// Task Column Component with Drag and Drop
const TaskColumn = ({ status, config, tasks, priorityColors, onTaskClick, onStatusUpdate }) => {
  return (
    <div className={`${config.color} rounded-lg p-4 h-fit min-h-[400px]`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900">{config.title}</h3>
        <Badge className={`${config.badge} text-white`}>
          {tasks.length}
        </Badge>
      </div>
      
      <Droppable droppableId={status} key={status}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`space-y-3 min-h-[300px] transition-colors ${
              snapshot.isDraggingOver ? 'bg-opacity-50 column-drag-over' : ''
            }`}
          >
            {tasks.map((task, index) => (
              <TaskCard
                key={task.id}
                task={task}
                index={index}
                priorityColors={priorityColors}
                onClick={() => onTaskClick(task)}
                onStatusUpdate={onStatusUpdate}
                currentStatus={status}
              />
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
};

// Task Card Component with Drag and Drop
const TaskCard = ({ task, index, priorityColors, onClick, onStatusUpdate, currentStatus }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString();
    } catch {
      return '';
    }
  };

  const getNextStatus = (status) => {
    const statusOrder = ['pending', 'in_progress', 'waiting', 'completed'];
    const currentIndex = statusOrder.indexOf(status);
    return currentIndex < statusOrder.length - 1 ? statusOrder[currentIndex + 1] : null;
  };

  const getPrevStatus = (status) => {
    const statusOrder = ['pending', 'in_progress', 'waiting', 'completed'];
    const currentIndex = statusOrder.indexOf(status);
    return currentIndex > 0 ? statusOrder[currentIndex - 1] : null;
  };

  const nextStatus = getNextStatus(currentStatus);
  const prevStatus = getPrevStatus(currentStatus);

  return (
    <Draggable draggableId={String(task.id)} index={index} key={task.id}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
        >
          <Card
            className={`cursor-pointer hover:shadow-md transition-all duration-200 ${
              snapshot.isDragging 
                ? 'shadow-lg rotate-1 opacity-90 z-50 task-card-dragging' 
                : 'hover:shadow-md'
            }`}
          >
            <CardContent className="p-4">
              <div className="space-y-3">
                <div className="flex items-start justify-between" onClick={(e) => {
                  if (!snapshot.isDragging) {
                    e.stopPropagation();
                    onClick();
                  }
                }}>
                  <h4 className="font-medium text-gray-900 text-sm leading-tight">{task.title}</h4>
                  <Badge className={`text-xs ${priorityColors[task.priority] || priorityColors.normal}`}>
                    {task.priority}
                  </Badge>
                </div>
                
                {task.description && (
                  <p 
                    className="text-gray-600 text-sm line-clamp-2" 
                    onClick={(e) => {
                      if (!snapshot.isDragging) {
                        e.stopPropagation();
                        onClick();
                      }
                    }}
                  >
                    {task.description}
                  </p>
                )}
                
                <div className="flex items-center justify-between" onClick={(e) => {
                  if (!snapshot.isDragging) {
                    e.stopPropagation();
                    onClick();
                  }
                }}>
                  <div className="flex items-center gap-2">
                    <Avatar className="w-6 h-6">
                      <AvatarImage src={task.assignee_avatar} />
                      <AvatarFallback className="text-xs">
                        {task.assignee_name?.charAt(0) || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-xs text-gray-600">{task.assignee_name}</span>
                  </div>
                  
                  {task.due_date && (
                    <span className="text-xs text-gray-500">
                      {formatDate(task.due_date)}
                    </span>
                  )}
                </div>

                {/* Status Update Buttons - only show when not dragging */}
                {!snapshot.isDragging && (
                  <div className="flex gap-1">
                    {prevStatus && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs px-2 py-1 h-6"
                        onClick={(e) => {
                          e.stopPropagation();
                          onStatusUpdate(task.id, prevStatus);
                        }}
                      >
                        ← {prevStatus.replace('_', ' ')}
                      </Button>
                    )}
                    {nextStatus && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs px-2 py-1 h-6"
                        onClick={(e) => {
                          e.stopPropagation();
                          onStatusUpdate(task.id, nextStatus);
                        }}
                      >
                        {nextStatus.replace('_', ' ')} →
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </Draggable>
  );
};

// Create Task Modal
const CreateTaskModal = ({ users, departments, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assignee_id: '',
    department_id: '',
    due_date: '',
    priority: 'normal',
    estimated_hours: '',
    tags: []
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData };
      if (submitData.due_date) {
        submitData.due_date = new Date(submitData.due_date).toISOString();
      }
      if (submitData.estimated_hours) {
        submitData.estimated_hours = parseFloat(submitData.estimated_hours);
      }

      await axios.post('/tasks', submitData);
      toast.success('Task created successfully');
      onSuccess();
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-xl font-semibold mb-4">Create New Task</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="title">Task Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              required
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="assignee">Assign to *</Label>
            <Select value={formData.assignee_id} onValueChange={(value) => setFormData({...formData, assignee_id: value})}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select assignee" />
              </SelectTrigger>
              <SelectContent>
                {users.map(user => (
                  <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="priority">Priority</Label>
            <Select value={formData.priority} onValueChange={(value) => setFormData({...formData, priority: value})}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="due_date">Due Date</Label>
            <Input
              id="due_date"
              type="datetime-local"
              value={formData.due_date}
              onChange={(e) => setFormData({...formData, due_date: e.target.value})}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="estimated_hours">Estimated Hours</Label>
            <Input
              id="estimated_hours"
              type="number"
              step="0.5"
              value={formData.estimated_hours}
              onChange={(e) => setFormData({...formData, estimated_hours: e.target.value})}
              className="mt-1"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button type="submit" className="flex-1 bg-indigo-600 hover:bg-indigo-700">
              Create Task
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Task Details Modal
const TaskDetailsModal = ({ task, onClose, onSuccess }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');

  useEffect(() => {
    fetchComments();
  }, [task.id]);

  const fetchComments = async () => {
    try {
      const response = await axios.get(`/tasks/${task.id}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    }
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      await axios.post(`/tasks/${task.id}/comments`, { comment: newComment });
      setNewComment('');
      fetchComments();
      toast.success('Comment added');
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not set';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return 'Invalid date';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-semibold">{task.title}</h2>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>

        <div className="space-y-6">
          {/* Task Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Status</Label>
              <Badge className="mt-1 inline-block">{task.status.replace('_', ' ')}</Badge>
            </div>
            <div>
              <Label>Priority</Label>
              <Badge className="mt-1 inline-block">{task.priority}</Badge>
            </div>
            <div>
              <Label>Assignee</Label>
              <p className="mt-1">{task.assignee_name}</p>
            </div>
            <div>
              <Label>Due Date</Label>
              <p className="mt-1">{formatDate(task.due_date)}</p>
            </div>
            <div>
              <Label>Estimated Hours</Label>
              <p className="mt-1">{task.estimated_hours || 'Not set'}</p>
            </div>
            <div>
              <Label>Actual Hours</Label>
              <p className="mt-1">{task.actual_hours || 'Not logged'}</p>
            </div>
          </div>

          {/* Description */}
          {task.description && (
            <div>
              <Label>Description</Label>
              <p className="mt-1 text-gray-700">{task.description}</p>
            </div>
          )}

          {/* Comments Section */}
          <div>
            <Label>Comments ({comments.length})</Label>
            <div className="mt-2 space-y-3 max-h-60 overflow-y-auto">
              {comments.map(comment => (
                <div key={comment.id} className="p-3 bg-gray-50 rounded">
                  <p className="text-sm">{comment.comment}</p>
                  <p className="text-xs text-gray-500 mt-1">{formatDate(comment.created_at)}</p>
                </div>
              ))}
            </div>
            
            <form onSubmit={addComment} className="mt-4 flex gap-2">
              <Input
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                className="flex-1"
              />
              <Button type="submit">Add</Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

// Time Tracker Component with GPS and Dashboard
const TimeTracker = () => {
  const { user } = useAuth();
  const [todayCheckin, setTodayCheckin] = useState(null);
  const [recentCheckins, setRecentCheckins] = useState([]);
  const [weeklyStats, setWeeklyStats] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [loading, setLoading] = useState(true); // Add loading state
  const [checkinData, setCheckinData] = useState({
    status: 'present',
    location: '',
    notes: '',
    mood_rating: 3
  });

  useEffect(() => {
    console.log('TimeTracker component mounted, fetching data...');
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchTodayCheckin(),
        fetchRecentCheckins(),
        fetchWeeklyStats()
      ]);
      getCurrentLocation();
      setLoading(false);
    };
    loadData();
  }, []);

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      setLocationLoading(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCurrentLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
          setLocationLoading(false);
        },
        (error) => {
          console.error('Geolocation error:', error);
          setLocationLoading(false);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    }
  };

  const fetchTodayCheckin = async () => {
    try {
      console.log('Fetching today checkin...');
      const response = await axios.get('/checkins/today');
      console.log('Today checkin response:', response.data);
      setTodayCheckin(response.data);
    } catch (error) {
      console.error('Failed to fetch today checkin:', error);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }
    }
  };

  const fetchRecentCheckins = async () => {
    try {
      console.log('Fetching recent checkins...');
      const response = await axios.get('/checkins/recent');
      console.log('Recent checkins response:', response.data);
      if (response.data) {
        setRecentCheckins(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch recent checkins:', error);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }
    }
  };

  const fetchWeeklyStats = async () => {
    try {
      console.log('Fetching weekly stats...');
      const response = await axios.get('/checkins/weekly-stats');
      console.log('Weekly stats response:', response.data);
      if (response.data) {
        setWeeklyStats(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch weekly stats:', error);
      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }
    }
  };

  const handleCheckin = async () => {
    try {
      const checkinPayload = {
        ...checkinData,
        checkin_location: currentLocation
      };
      
      await axios.post('/checkins', checkinPayload);
      toast.success('Checked in successfully!');
      fetchTodayCheckin();
      fetchRecentCheckins();
      fetchWeeklyStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check in');
    }
  };

  const handleCheckout = async () => {
    try {
      const checkoutPayload = {
        checkout_location: currentLocation
      };
      
      const response = await axios.put('/checkins/checkout', checkoutPayload);
      toast.success(`Checked out successfully! Worked ${Math.floor(response.data.work_duration_minutes / 60)}h ${response.data.work_duration_minutes % 60}m`);
      fetchTodayCheckin();
      fetchRecentCheckins();
      fetchWeeklyStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check out');
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '--:--';
    return new Date(dateStr).toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '0h 0m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const getLocationText = (location) => {
    if (location && location.latitude && location.longitude) {
      return `${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}`;
    }
    return 'Location not available';
  };

  const getMoodEmoji = (rating) => {
    const moods = ['😢', '😕', '😐', '😊', '😄'];
    return moods[rating - 1] || '😐';
  };

  const getStatusColor = (status) => {
    const colors = {
      present: 'bg-green-100 text-green-800',
      remote: 'bg-blue-100 text-blue-800', 
      sick: 'bg-red-100 text-red-800',
      vacation: 'bg-purple-100 text-purple-800',
      late: 'bg-orange-100 text-orange-800',
      personal_day: 'bg-gray-100 text-gray-800',
      half_day: 'bg-yellow-100 text-yellow-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {loading ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading Time Tracker...</p>
          </div>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Time Tracker</h1>
              <p className="text-gray-600 mt-1">Track your work hours and manage attendance</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Current Time</p>
              <p className="text-2xl font-mono font-semibold text-gray-900">
                {new Date().toLocaleTimeString('en-US', { hour12: false })}
              </p>
            </div>
          </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Check-in/out Card */}
          <div className="lg:col-span-2">
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  Today's Attendance
                </CardTitle>
                <CardDescription>{new Date().toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}</CardDescription>
              </CardHeader>
              <CardContent>
                {todayCheckin ? (
                  <div className="space-y-6">
                    {/* Current Status */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center">
                        <Label className="text-sm text-gray-500">Status</Label>
                        <Badge className={`mt-1 ${getStatusColor(todayCheckin.status)}`}>
                          {todayCheckin.status.replace('_', ' ')}
                        </Badge>
                      </div>
                      <div className="text-center">
                        <Label className="text-sm text-gray-500">Check-in</Label>
                        <p className="text-lg font-mono font-semibold mt-1">
                          {formatTime(todayCheckin.checkin_time)}
                        </p>
                      </div>
                      <div className="text-center">
                        <Label className="text-sm text-gray-500">Check-out</Label>
                        <p className="text-lg font-mono font-semibold mt-1">
                          {formatTime(todayCheckin.checkout_time)}
                        </p>
                      </div>
                      <div className="text-center">
                        <Label className="text-sm text-gray-500">Duration</Label>
                        <p className="text-lg font-semibold mt-1 text-green-600">
                          {formatDuration(todayCheckin.work_duration_minutes)}
                        </p>
                      </div>
                    </div>

                    {/* Location and Mood */}
                    {(todayCheckin.checkin_location || todayCheckin.mood_rating) && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                        {todayCheckin.checkin_location && (
                          <div>
                            <Label className="text-sm text-gray-500">Check-in Location</Label>
                            <p className="text-sm text-gray-700 mt-1">
                              📍 {getLocationText(todayCheckin.checkin_location)}
                            </p>
                            {todayCheckin.location && (
                              <p className="text-xs text-gray-500 mt-1">{todayCheckin.location}</p>
                            )}
                          </div>
                        )}
                        {todayCheckin.mood_rating && (
                          <div>
                            <Label className="text-sm text-gray-500">Mood Rating</Label>
                            <p className="text-lg mt-1">
                              {getMoodEmoji(todayCheckin.mood_rating)} {todayCheckin.mood_rating}/5
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Notes */}
                    {todayCheckin.notes && (
                      <div className="pt-4 border-t">
                        <Label className="text-sm text-gray-500">Notes</Label>
                        <p className="text-gray-700 mt-1">{todayCheckin.notes}</p>
                      </div>
                    )}

                    {/* Checkout Button */}
                    {!todayCheckin.checkout_time && (
                      <div className="pt-4 border-t">
                        <Button 
                          onClick={handleCheckout} 
                          className="w-full bg-red-600 hover:bg-red-700"
                          disabled={locationLoading}
                        >
                          {locationLoading ? 'Getting Location...' : 'Check Out'}
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="text-center py-8">
                      <div className="text-6xl mb-4">⏰</div>
                      <p className="text-gray-600 text-lg">You haven't checked in today yet.</p>
                    </div>
                    
                    <div className="space-y-4">
                      {/* Status Selection */}
                      <div>
                        <Label htmlFor="status">Work Status</Label>
                        <Select value={checkinData.status} onValueChange={(value) => setCheckinData({...checkinData, status: value})}>
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="present">🏢 Present (Office)</SelectItem>
                            <SelectItem value="remote">🏠 Remote (Home)</SelectItem>
                            <SelectItem value="sick">🤒 Sick Leave</SelectItem>
                            <SelectItem value="vacation">🏖️ Vacation</SelectItem>
                            <SelectItem value="late">⏰ Late Arrival</SelectItem>
                            <SelectItem value="personal_day">👤 Personal Day</SelectItem>
                            <SelectItem value="half_day">⏱️ Half Day</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Location */}
                      <div>
                        <Label htmlFor="location">Location Description (Optional)</Label>
                        <Input
                          id="location"
                          value={checkinData.location}
                          onChange={(e) => setCheckinData({...checkinData, location: e.target.value})}
                          placeholder="e.g., Home Office, Client Site, Conference Room A"
                          className="mt-1"
                        />
                        {currentLocation && (
                          <p className="text-xs text-gray-500 mt-1">
                            📍 GPS: {getLocationText(currentLocation)}
                          </p>
                        )}
                      </div>

                      {/* Mood Rating */}
                      <div>
                        <Label>How are you feeling today? {getMoodEmoji(checkinData.mood_rating)} ({checkinData.mood_rating}/5)</Label>
                        <input
                          type="range"
                          min="1"
                          max="5"
                          value={checkinData.mood_rating}
                          onChange={(e) => setCheckinData({...checkinData, mood_rating: parseInt(e.target.value)})}
                          className="mt-2 w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>😢 Bad</span>
                          <span>😄 Great</span>
                        </div>
                      </div>

                      {/* Notes */}
                      <div>
                        <Label htmlFor="notes">Notes (Optional)</Label>
                        <Textarea
                          id="notes"
                          value={checkinData.notes}
                          onChange={(e) => setCheckinData({...checkinData, notes: e.target.value})}
                          placeholder="Any notes for today's work..."
                          className="mt-1"
                          rows={3}
                        />
                      </div>

                      <Button 
                        onClick={handleCheckin} 
                        className="w-full bg-green-600 hover:bg-green-700"
                        disabled={locationLoading}
                      >
                        {locationLoading ? 'Getting Location...' : 'Check In'}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar with Stats and Recent Activity */}
          <div className="space-y-6">
            {/* Weekly Stats */}
            {weeklyStats && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">This Week</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Days Worked</span>
                      <span className="font-semibold">{weeklyStats.days_worked || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Hours</span>
                      <span className="font-semibold">{formatDuration(weeklyStats.total_minutes || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Avg Daily</span>
                      <span className="font-semibold">{formatDuration(weeklyStats.avg_daily_minutes || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Mood Average</span>
                      <span className="font-semibold">
                        {getMoodEmoji(Math.round(weeklyStats.avg_mood || 3))} {(weeklyStats.avg_mood || 0).toFixed(1)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Current Location */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Current Location</CardTitle>
              </CardHeader>
              <CardContent>
                {currentLocation ? (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">📍 GPS Coordinates</p>
                    <p className="font-mono text-xs">{getLocationText(currentLocation)}</p>
                    <p className="text-xs text-gray-500">
                      Accuracy: ±{Math.round(currentLocation.accuracy)}m
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <Button 
                      variant="outline" 
                      onClick={getCurrentLocation}
                      disabled={locationLoading}
                    >
                      {locationLoading ? 'Getting Location...' : 'Get Current Location'}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Recent Activity</CardTitle>
              </CardHeader>
              <CardContent>
                {recentCheckins.length > 0 ? (
                  <div className="space-y-3">
                    {recentCheckins.slice(0, 5).map((checkin, index) => (
                      <div key={checkin.id || index} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                        <div>
                          <p className="text-sm font-medium">{formatDate(checkin.checkin_date)}</p>
                          <p className="text-xs text-gray-500">
                            {formatTime(checkin.checkin_time)} - {formatTime(checkin.checkout_time)}
                          </p>
                        </div>
                        <div className="text-right">
                          <Badge className={`text-xs ${getStatusColor(checkin.status)}`}>
                            {checkin.status.replace('_', ' ')}
                          </Badge>
                          <p className="text-xs text-gray-500 mt-1">
                            {formatDuration(checkin.work_duration_minutes)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm text-center py-4">No recent activity</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
        </div>
      )}
    </div>
  );
};

// Main App Component
const App = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false); // Add auth check state

  useEffect(() => {
    // Check for existing auth
    const checkAuth = () => {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');
      
      if (token && savedUser) {
        try {
          const parsedUser = JSON.parse(savedUser);
          setUser(parsedUser);
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        } catch (error) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      }
      setAuthChecked(true);
      setLoading(false);
    };

    checkAuth();
  }, []);

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  if (loading || !authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Login onLogin={setUser} />;
  }

  return (
    <AuthContext.Provider value={{ user, logout }}>
      <BrowserRouter>
        <div className="min-h-screen bg-gray-50">
        {/* Navigation */}
        <nav className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16">
              <div className="flex items-center">
                <img 
                  src="https://images.unsplash.com/photo-1555212697-194d092e3b8f" 
                  alt="TaskTime" 
                  className="w-8 h-8 rounded object-cover mr-3"
                />
                <span className="text-xl font-bold text-gray-900">TaskTime</span>
              </div>
              
              <div className="flex items-center gap-6">
                {/* URL-based Navigation */}
                <div className="flex items-center bg-gray-100 rounded-lg p-1">
                  <NavLink
                    to="/"
                    className={({ isActive }) =>
                      `px-4 py-2 rounded-md font-medium transition-all text-sm ${
                        isActive
                          ? 'bg-white text-indigo-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`
                    }
                  >
                    Task Board
                  </NavLink>
                  <NavLink
                    to="/time-tracker"
                    className={({ isActive }) =>
                      `px-4 py-2 rounded-md font-medium transition-all text-sm ml-1 ${
                        isActive
                          ? 'bg-white text-indigo-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      }`
                    }
                  >
                    Time Tracker
                  </NavLink>
                </div>
                
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-700">Welcome, {user.name}</span>
                  <Button variant="outline" onClick={logout} size="sm">
                    Logout
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </nav>

        {/* Main Content with Routes */}
        <main>
          <Routes>
            <Route path="/" element={<TaskBoard />} />
            <Route path="/task-board" element={<Navigate to="/" replace />} />
            <Route path="/time-tracker" element={<TimeTracker />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
        
        {/* Toast notifications */}
        <Toaster />
      </div>
      </BrowserRouter>
    </AuthContext.Provider>
  );
};

export default App;